import { useStore } from '../store/StoreContext.jsx';
import { useLayout } from '../store/LayoutContext.jsx';
import { getDashboardMetrics, formatDollars, savingsRatioClass, dtiClass } from '../store/selectors.js';
import { PQ_TABS, tabProgress } from '../schema/pqTabs.js';
import { coverageSummary } from './TabBar.jsx';
import Icon from './Icon.jsx';
import ViewButton from './ViewButton.jsx';

function pct(n, dec = 1) {
  if (n == null || !isFinite(n)) return '0%';
  return n.toFixed(dec) + '%';
}

function shortDollars(n) {
  if (n == null || isNaN(n)) return '$0';
  const abs = Math.abs(Math.round(n));
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return sign + '$' + (abs / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000) return sign + '$' + (abs / 1_000).toFixed(1) + 'K';
  return sign + '$' + abs.toLocaleString('en-US');
}

// On a fresh record every figure is 0 and savingsRatioClass(0) is 'bad' — so
// the rail would open a meeting with a red "Savings Ratio 0.0%" and $0 KPIs,
// which reads as a verdict if the client can see the screen (R11). Gate the
// balance figures on any assets/liabilities and the income ratios on any
// income, so the rail stays a quiet progress companion until there's data.
function dataState(m) {
  const hasBalance = m.totalAssets > 0 || m.totalLiabilities > 0;
  const hasIncome = m.totalIncome > 0;
  return { hasBalance, hasIncome, anyData: hasBalance || hasIncome };
}
const DASH = '—';

// A one-word verdict paired with each ratio's color, so the good/warn/bad
// status isn't carried by hue alone (R13). Phrased to read correctly for both
// ratios (a low savings rate and a high DTI are both "Off track").
const RATIO_VERDICT = { good: 'On track', warn: 'Watch', bad: 'Off track' };

// Yearly Cash Flow card — shared by the compact and full rail so the numbers
// stay identical in both states.
function CashFlowCard({ m }) {
  return (
    <div className="rail-card">
      <div className="rail-card-head">
        <span className="rail-card-title">Yearly Cash Flow</span>
        <span className="rail-card-eyebrow">est.</span>
      </div>
      <div className="flow">
        <div className="flow-row income">
          <span className="lhs">
            <span className="arrow">+</span> Income
          </span>
          <span className="v">{formatDollars(m.totalIncome)}</span>
        </div>
        <div className="flow-row outflow">
          <span className="lhs">
            <span className="arrow">−</span> Savings
          </span>
          <span className="v">{formatDollars(m.totalSavings)}</span>
        </div>
        <div className="flow-row outflow">
          <span className="lhs">
            <span className="arrow">−</span> Taxes
          </span>
          <span className="v">{formatDollars(m.totalTax)}</span>
        </div>
        <div className="flow-row outflow">
          <span className="lhs">
            <span className="arrow">−</span> Liability payments
          </span>
          <span className="v">{formatDollars(m.liabilityPayments)}</span>
        </div>
        <div className="flow-row outflow">
          <span className="lhs">
            <span className="arrow">−</span> Living &amp; other
          </span>
          <span className="v">{formatDollars(m.nonLiabilityExpenses)}</span>
        </div>
        <div className={'flow-total ' + (m.cashFlow < 0 ? 'neg' : '')}>
          <span className="lbl">Net Cash Flow</span>
          <span className="v">{formatDollars(m.cashFlow)}</span>
        </div>
      </div>
    </div>
  );
}

// The three totals an advisor always wants in view.
function TotalsCard({ m }) {
  const { hasBalance } = dataState(m);
  const bal = v => (hasBalance ? formatDollars(v) : DASH);
  return (
    <div className="rail-card">
      <div className="mini-kpi-list">
        <div className="mini-kpi">
          <span className="mini-kpi-label">Total Assets</span>
          <span className="mini-kpi-val">{bal(m.totalAssets)}</span>
        </div>
        <div className="mini-kpi">
          <span className="mini-kpi-label">Total Liabilities</span>
          <span className="mini-kpi-val">{bal(m.totalLiabilities)}</span>
        </div>
        <div className="mini-kpi is-net">
          <span className="mini-kpi-label">Net Worth</span>
          <span className="mini-kpi-val">{bal(m.totalNetWorth)}</span>
        </div>
      </div>
    </div>
  );
}

// Compact rail: the totals and the full cash-flow breakdown in a slim
// footprint, the same on every page. Everything else lives in the full rail,
// revealed on hover or by pinning it open.
function CompactRail({ m }) {
  return (
    <div className="rail-compact">
      <TotalsCard m={m} />
      <CashFlowCard m={m} />
    </div>
  );
}

// Snapshot card: Net Worth + Balance Sheet
function SnapshotCard({ m }) {
  const { hasBalance, anyData } = dataState(m);
  const bal = v => (hasBalance ? formatDollars(v) : DASH);
  const balShort = v => (hasBalance ? shortDollars(v) : DASH);
  return (
    <div className="rail-card">
      <div className="kpi">
        <div className="kpi-label">Net Worth</div>
        <div className="kpi-value">{bal(m.totalNetWorth)}</div>
        <div className="kpi-sub">
          <span style={{ color: 'var(--ink-4)' }}>
            {anyData ? 'Live · auto-updates' : 'Totals appear as you enter assets and income.'}
          </span>
        </div>
      </div>

      <div className="rail-card-body" style={{ paddingTop: 12 }}>
        <div className="rail-card-title" style={{ marginBottom: 6 }}>
          Balance Sheet
        </div>
        <div className="bs-row">
          <span className="lhs">
            <span className="swatch swatch-invest" /> Investable
          </span>
          <span className="v">{bal(m.investableAssets)}</span>
        </div>
        <div className="bs-row">
          <span className="lhs">
            <span className="swatch swatch-realestate" /> Real Estate
          </span>
          <span className="v">{bal(m.realEstateAssets)}</span>
        </div>
        <div className="bs-row">
          <span className="lhs">
            <span className="swatch swatch-business" /> Business / Other
          </span>
          <span className="v">{bal(m.businessOtherAssets)}</span>
        </div>
        <div className="bs-total">
          <span>Total Assets</span>
          <span className="v">{bal(m.totalAssets)}</span>
        </div>
      </div>

      <div className="bs-totals-grid">
        <div className="bs-totals-cell">
          <div className="lbl">Liabilities</div>
          <div className="val">{balShort(m.totalLiabilities)}</div>
        </div>
        <div className="bs-totals-cell is-net">
          <div className="lbl">Net Worth</div>
          <div className="val">{balShort(m.totalNetWorth)}</div>
        </div>
      </div>
    </div>
  );
}

// Tax Allocation card: the tax triangle as a stacked bar.
function TaxAllocationCard({ m }) {
  const total = m.taxFree + m.taxable + m.taxDeferred;
  const segPct = v => (total > 0 ? (v / total) * 100 : 0);
  return (
    <div className="rail-card">
      <div className="rail-card-head">
        <span className="rail-card-title">Tax Allocation</span>
        <span className="rail-card-eyebrow">{shortDollars(total)}</span>
      </div>
      <div className="triangle-wrap">
        <div className="triangle-bar">
          <div
            className="triangle-seg s-deferred"
            style={{ width: segPct(m.taxDeferred) + '%' }}
          >
            {total > 0 ? Math.round(segPct(m.taxDeferred)) + '%' : ''}
          </div>
          <div
            className="triangle-seg s-taxable"
            style={{ width: segPct(m.taxable) + '%' }}
          >
            {total > 0 ? Math.round(segPct(m.taxable)) + '%' : ''}
          </div>
          <div
            className="triangle-seg s-free"
            style={{ width: segPct(m.taxFree) + '%' }}
          >
            {total > 0 ? Math.round(segPct(m.taxFree)) + '%' : ''}
          </div>
        </div>
        <div className="triangle-legend">
          <div className="tri-leg l-deferred">
            <div className="tri-leg-label">Tax-Deferred</div>
            <div className="tri-leg-val">{shortDollars(m.taxDeferred)}</div>
            <div className="tri-leg-pct">
              {Math.round(segPct(m.taxDeferred))}%
            </div>
          </div>
          <div className="tri-leg l-taxable">
            <div className="tri-leg-label">Taxable</div>
            <div className="tri-leg-val">{shortDollars(m.taxable)}</div>
            <div className="tri-leg-pct">
              {Math.round(segPct(m.taxable))}%
            </div>
          </div>
          <div className="tri-leg l-free">
            <div className="tri-leg-label">Tax-Free</div>
            <div className="tri-leg-val">{shortDollars(m.taxFree)}</div>
            <div className="tri-leg-pct">{Math.round(segPct(m.taxFree))}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Savings ratio and debt-to-income, each with its verdict.
function RatiosCard({ m }) {
  const { hasIncome } = dataState(m);
  // Empty until income exists, so the rail stays quiet on a blank record (R11).
  const savingsStatus = hasIncome ? savingsRatioClass(m.savingsRatio) : '';
  const dtiStatus = hasIncome ? dtiClass(m.dti) : '';
  return (
    <div className="rail-card">
      <div className="rail-card-head">
        <span className="rail-card-title">Ratios</span>
      </div>
      <div className="ratios">
        <div className={'ratio-cell ' + savingsStatus}>
          <div className="ratio-label">Savings Ratio</div>
          <div className="ratio-value-row">
            <span className="ratio-value">{hasIncome ? pct(m.savingsRatio) : DASH}</span>
            {savingsStatus && <span className="ratio-verdict">{RATIO_VERDICT[savingsStatus]}</span>}
          </div>
          <div className="ratio-bar">
            <span style={{ width: (hasIncome ? Math.min(m.savingsRatio * 3, 100) : 0) + '%' }} />
          </div>
        </div>
        <div className={'ratio-cell ' + dtiStatus}>
          <div className="ratio-label">Debt-to-Income</div>
          <div className="ratio-value-row">
            <span className="ratio-value">{hasIncome ? pct(m.dti) : DASH}</span>
            {dtiStatus && <span className="ratio-verdict">{RATIO_VERDICT[dtiStatus]}</span>}
          </div>
          <div className="ratio-bar">
            <span style={{ width: (hasIncome ? Math.min(m.dti * 1.5, 100) : 0) + '%' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Where the questionnaire stands, page by page. Each row is a link to that
// page, so the rail doubles as the index the side nav used to be.
function ProgressCard({ data }) {
  const { activeTab, setActiveTab } = useLayout();
  return (
    <div className="rail-card">
      <div className="rail-card-head">
        <span className="rail-card-title">Pages</span>
      </div>
      <div className="rail-pages">
        {PQ_TABS.map((t, i) => {
          const p = tabProgress(t, data);
          return (
            <ViewButton
              key={t.id}
              className={'rail-page' + (t.id === activeTab ? ' is-active' : '') + (p >= 100 ? ' is-complete' : '')}
              onClick={() => setActiveTab(t.id)}
              title={`${t.label} (${p}% complete)`}
            >
              <span className="rail-page-num">{i + 1}</span>
              <span className="rail-page-label">{t.label}</span>
              <span className="rail-page-pct">{p}%</span>
              <span className="rail-page-bar" aria-hidden="true">
                <span style={{ width: `${p}%` }} />
              </span>
            </ViewButton>
          );
        })}
      </div>
    </div>
  );
}

// What the insurance table adds up to, and which estate documents are on
// record. "None" is an answer here, not an absence: the field records that
// the question was asked.
function CoverageCard({ data }) {
  const c = coverageSummary(data);
  const estate = Array.isArray(data.goals?.estatePlan)
    ? data.goals.estatePlan
    : String(data.goals?.estatePlan || '').split(',').map(s => s.trim()).filter(Boolean);
  return (
    <div className="rail-card">
      <div className="rail-card-head">
        <span className="rail-card-title">Coverage</span>
        <span className="rail-card-eyebrow">{c.count ? `${c.count} ${c.count === 1 ? 'policy' : 'policies'}` : ''}</span>
      </div>
      <div className="mini-kpi-list">
        <div className="mini-kpi">
          <span className="mini-kpi-label">Life death benefit</span>
          <span className="mini-kpi-val">{c.count ? formatDollars(c.deathBenefit) : DASH}</span>
        </div>
        <div className="mini-kpi">
          <span className="mini-kpi-label">Annual premiums</span>
          <span className="mini-kpi-val">{c.count ? formatDollars(c.premiums) : DASH}</span>
        </div>
      </div>
      <div className="rail-card-body" style={{ paddingTop: 10 }}>
        <div className="rail-card-title" style={{ marginBottom: 6 }}>Estate Plan</div>
        {estate.length ? (
          <div className="rail-chips">
            {estate.map(e => <span key={e} className="rail-chip">{e}</span>)}
          </div>
        ) : (
          <div className="rail-muted">Not recorded yet.</div>
        )}
      </div>
    </div>
  );
}

// The full rail follows the open page: the balance sheet and tax allocation
// beside the assets, the cash flow and ratios beside the income, coverage
// beside the insurance, and the page index beside the household details,
// where there is nothing to add up yet.
function FullRail({ tabId, data, m }) {
  switch (tabId) {
    case 'balance':
      return (<><SnapshotCard m={m} /><TaxAllocationCard m={m} /></>);
    case 'cashflow':
      return (<><CashFlowCard m={m} /><RatiosCard m={m} /><TotalsCard m={m} /></>);
    case 'protection':
      return (<><CoverageCard data={data} /><TotalsCard m={m} /><ProgressCard data={data} /></>);
    default:
      return (<><ProgressCard data={data} /><SnapshotCard m={m} /></>);
  }
}

export default function PQDashboard() {
  const { data } = useStore();
  const { activeTab, railCollapsed, railHidden, toggleRail, hideRail, showRail } = useLayout();
  const m = getDashboardMetrics(data);

  // Hidden: the rail is gone and the form has its width; a slim tab on the
  // right edge brings it back.
  if (railHidden) {
    return (
      <ViewButton
        className="rail-show-tab"
        onClick={showRail}
        aria-label="Show insights panel"
        title="Show insights"
      >
        <Icon name="chevron-left" size={13} />
        <span className="rail-show-tab-text">Insights</span>
      </ViewButton>
    );
  }

  return (
    <aside className="rail" data-rail-for={activeTab}>
      <div className="rail-controls">
        {/* Layout only, so they stay usable in view mode (see ViewButton). */}
        <ViewButton
          className="panel-toggle rail-toggle"
          onClick={toggleRail}
          aria-pressed={!railCollapsed}
          aria-label={railCollapsed ? 'Expand insights panel' : 'Collapse insights panel'}
          title={railCollapsed ? 'Pin insights open' : 'Collapse insights'}
        >
          <Icon name={railCollapsed ? 'chevron-left' : 'chevron-right'} size={15} />
        </ViewButton>
        <ViewButton
          className="panel-toggle rail-hide"
          onClick={hideRail}
          aria-label="Hide insights panel"
          title="Hide insights"
        >
          <Icon name="x" size={13} />
        </ViewButton>
      </div>

      <CompactRail m={m} />

      <div className="rail-full">
        <FullRail tabId={activeTab} data={data} m={m} />
      </div>
    </aside>
  );
}
